// web/src/features/dashboard/Dashboard.jsx
import React, { useMemo, useState, useEffect } from 'react'
import ComplianceDashboard from './ComplianceDashboard'
import { Link, useSearchParams } from 'react-router-dom'

// Firestore data (one-shot reads; no realtime)
import { listCafs } from '@/services/firestore/cafService'
import { listCommunitiesFS } from '@/services/firestore/communityService'
import { getSession } from '@/lib/auth'
import { listPmTemplatesAllFS } from '@/services/firestore/pmService'
import { getActiveLimitsForWorkflowFS } from '@/services/firestore/approvalLimitsService'

// near the top of each file (optional helper)
const HEADER_STYLE = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginTop: 14,
  marginBottom: 12,
}

/** ------- Utils ------- */
function hasMin3WithPdf(caf) {
  const list = Array.isArray(caf.proposals) ? caf.proposals : []
  if (list.length < 3) return false
  return list.every(p => p && p.file && (p.file.dataUrl || p.file.downloadURL))
}

const STATUS_OPTIONS = [
  'All',
  'draft',
  'submitted',
  'in_review',
  'awaiting_approval',
  'approved',
  'changes_requested',
  'complete',
]

function csvEscape(v) {
  if (v == null) return ''
  const s = String(v)
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`
  return s
}

// Firestore Timestamp | string | Date -> ISO string
function normalizeCreatedAt(v) {
  if (!v) return ''
  if (v.seconds) return new Date(v.seconds * 1000).toISOString()
  if (typeof v === 'string') return v
  try {
    return new Date(v).toISOString()
  } catch {
    return ''
  }
}

function toDateOrNull(v) {
  if (!v) return null
  if (v.seconds) return new Date(v.seconds * 1000)
  const d = new Date(v)
  return Number.isNaN(d.getTime()) ? null : d
}

function daysBetween(start, end) {
  if (!start || !end) return null
  const ms = end.getTime() - start.getTime()
  if (!Number.isFinite(ms) || ms < 0) return null
  return ms / (1000 * 60 * 60 * 24)
}

function formatCurrency(amount) {
  const n = Number(amount || 0)
  if (!Number.isFinite(n)) return '$0'
  return `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`
}

/* ===== minimal scope helpers ===== */
function makeScope() {
  const sess = getSession() || {}
  const lvl = (sess.adminLevel || '').toString().toLowerCase()
  const isAdmin = lvl === 'admin' || lvl === 'sysconfig'
  const allowed = new Set(
    (Array.isArray(sess.communities) ? sess.communities : [])
      .map(s => (s || '').toString().trim())
      .filter(Boolean),
  )
  return { isAdmin, allowed }
}

/** Export CSV – now includes budget, actual, variance */
function exportCsv(rows, getCommunityName) {
  const header = [
    'CAF Number',
    'Community',
    'Title',
    'Status',
    'Stage',
    'Created At',
    'Proposal Count',
    'Recommended Vendor',
    'Recommended Price',
    'Planned Budget',
    'Actual Amount',
    'Variance (Actual - Budget)',
    'Created By',
  ]
  const lines = [header.join(',')]

  for (const x of rows) {
    const props = x.proposals || []
    const rec = props.find(p => p.recommended)

    const rawPlanned =
      x.contractPrice ??
      x.projectBudget ??
      x.projectBudgetAmount ??
      null

    const rawActual =
      x.projectActual ??
      x.actualProjectAmount ??
      x.projectActualAmount ??
      null

    let planned = ''
    let actual = ''
    let variance = ''

    const pNum = Number(rawPlanned)
    const aNum = Number(rawActual)

    const hasPlanned =
      rawPlanned !== null &&
      rawPlanned !== undefined &&
      !Number.isNaN(pNum)
    const hasActual =
      rawActual !== null &&
      rawActual !== undefined &&
      !Number.isNaN(aNum)

    if (hasPlanned) planned = pNum
    if (hasActual) actual = aNum
    if (hasPlanned && hasActual) variance = aNum - pNum

    const line = [
      x.number,
      getCommunityName(x.communityCode),
      x.title,
      x.status,
      x.stage || '',
      normalizeCreatedAt(x.createdAt) || '',
      props.length,
      rec ? rec.vendorName : '',
      rec ? Number(rec.price || 0) : '',
      planned,
      actual,
      variance,
      x.createdByName || x.createdByEmail || x.createdBy || '',
    ]
      .map(csvEscape)
      .join(',')
    lines.push(line)
  }

  const blob = new Blob([lines.join('\n')], {
    type: 'text/csv;charset=utf-8;',
  })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  const dt = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')
  a.download = `caf-dashboard-export-${dt}.csv`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

/* ---------- PM timeline helpers for "Sent to Entrata" KPI ---------- */

function isPmSentToEntrataEvent(entry) {
  const e = String(entry?.event || '').toLowerCase().trim()
  return e.startsWith('pm sent to entrata')
}

function pmHasEntrataSendInRange(pm, start, end) {
  const tl = Array.isArray(pm?.timeline) ? pm.timeline : []
  return tl.some(t => {
    if (!isPmSentToEntrataEvent(t)) return false
    const dt = toDateOrNull(t.ts || t.when || t.createdAt)
    return dt && dt >= start && dt < end
  })
}

function DashboardTabs({compliance,setTab}) {return <div className="compliance-dashboard-list-tabs dashboard-view-tabs" role="tablist" aria-label="Dashboard views">{['Capital Projects','License Compliance'].map((label,index)=><button key={label} id={`dashboard-view-${index}`} role="tab" aria-selected={compliance===(index===1)} aria-controls="dashboard-view-panel" tabIndex={compliance===(index===1)?0:-1} onClick={()=>setTab(index===1)} onKeyDown={e=>{if(!['ArrowLeft','ArrowRight','Home','End'].includes(e.key))return;e.preventDefault();const next=e.key==='Home'?0:e.key==='End'?1:1-index;setTab(next===1);e.currentTarget.parentElement.children[next].focus()}}>{label}</button>)}</div>}
export default function Dashboard() {
 const [params,setParams]=useSearchParams();const compliance=params.get('tab')==='compliance';
 return <div><h2 style={HEADER_STYLE}>Dashboard</h2><DashboardTabs compliance={compliance} setTab={value=>setParams(value?{tab:'compliance'}:{})}/><div id="dashboard-view-panel" className="compliance-dashboard-folder-panel dashboard-view-panel" role="tabpanel" aria-labelledby={`dashboard-view-${compliance?1:0}`}>{compliance?<ComplianceDashboard/>:<OperationsDashboard/>}</div></div>;
}
function OperationsDashboard() {
  // ---------- Data load (Firestore) ----------
  const [cafItems, setCafItems] = useState([])
  const [communities, setCommunities] = useState([])
  const [pmTemplates, setPmTemplates] = useState([])
  const [stageOptions, setStageOptions] = useState(['All'])
  const [loading, setLoading] = useState(true)

  const { isAdmin, allowed } = useMemo(() => makeScope(), [])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        setLoading(true)
        const [cafs, comms, pmTmpls, limits] = await Promise.all([
          listCafs(),
          listCommunitiesFS(),
          listPmTemplatesAllFS().catch(() => []),
          getActiveLimitsForWorkflowFS().catch(() => []),
        ])

        if (cancelled) return

        const resolved = (comms || []).map(c => ({
          code: c.code || c.id || '',
          communityName: c.communityName || c.name || c.title || c.id || '',
        }))

        const scopedCommunities = isAdmin
          ? comms || []
          : (comms || []).filter(c => {
              const code = c.code || c.id || ''
              const name = c.communityName || c.name || c.title || c.id || ''
              return allowed.has(code) || allowed.has(name)
            })
        setCommunities(scopedCommunities)

        const scopedCafs = isAdmin
          ? cafs || []
          : (cafs || []).filter(x => {
              const cc = x.communityCode || ''
              const name = resolved.find(r => r.code === cc)?.communityName || cc
              return allowed.has(cc) || allowed.has(name)
            })
        setCafItems(scopedCafs)
        setPmTemplates(pmTmpls || [])

        const rolesFromLimits = (limits || [])
          .filter(l => l && l.isActive !== false && l.role)
          .sort((a, b) => {
            const ao = Number.isFinite(Number(a.order)) ? Number(a.order) : 9999
            const bo = Number.isFinite(Number(b.order)) ? Number(b.order) : 9999
            return ao - bo
          })
          .map(l => l.role)

        let nextStageOptions = ['All']

        if (rolesFromLimits.length) {
          nextStageOptions = ['All', ...rolesFromLimits]
        } else {
          const fromCafs = Array.from(
            new Set((cafs || []).map(c => c.stage).filter(Boolean)),
          )
          if (fromCafs.length) nextStageOptions = ['All', ...fromCafs]
        }

        setStageOptions(nextStageOptions)
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [isAdmin, allowed])

  const getCommunityName = useMemo(() => {
    const byCode = new Map()
    for (const c of communities) {
      const code = c.code || ''
      const name = c.communityName || c.name || c.title || c.id || code || '—'
      if (code) byCode.set(code, name)
    }
    return code => byCode.get(code) || code || '—'
  }, [communities])

  const commOptions = useMemo(() => {
    const base = communities.map(c => ({
      code: c.code || c.id || '',
      communityName: c.communityName || c.name || c.title || c.id || c.code || '',
    }))
    base.sort((a, b) => (a.communityName || '').localeCompare(b.communityName || ''))
    return [{ code: 'All', communityName: 'All Communities' }, ...base]
  }, [communities])

  // Maps to reconcile "code vs id" selection (because your dropdown uses c.code || c.id)
  const communityCodeToId = useMemo(() => {
    const m = new Map()
    for (const c of communities || []) {
      const code = c.code || ''
      const id = c.id || c.communityId || ''
      if (code && id) m.set(code, id)
    }
    return m
  }, [communities])

  const communityIdToCode = useMemo(() => {
    const m = new Map()
    for (const c of communities || []) {
      const code = c.code || ''
      const id = c.id || c.communityId || ''
      if (id && code) m.set(id, code)
    }
    return m
  }, [communities])

  // ---------- Signed-in user ----------
  const session = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('session') || 'null')
    } catch {
      return null
    }
  }, [])
  const myEmail = (session?.email || '').toLowerCase()

  // ---------- Filters ----------
  const [status, setStatus] = useState('All')
  const [stage, setStage] = useState('All')
  const [communityCode, setCommunityCode] = useState('All')
  const [q, setQ] = useState('')
  const [mineOnlyDrafts, setMineOnlyDrafts] = useState(false)
  const [pendingOnly, setPendingOnly] = useState(false)
  const [nonCompliantOnly, setNonCompliantOnly] = useState(false)

  // ---------- Apply CAF filters ----------
  const items = useMemo(() => {
    const qLower = q.trim().toLowerCase()
    return cafItems.filter(x => {
      const s = (x.status || '').toLowerCase()
      const stg = x.stage || '—'
      const cc = x.communityCode || ''
      const createdByEmail = (x.createdByEmail || x.createdBy || '').toLowerCase()

      if (s === 'void') return false

      const isPending = !['approved', 'complete', 'draft'].includes(s)
      const isNonCompliant = !hasMin3WithPdf(x)
      if (pendingOnly && !isPending) return false
      if (nonCompliantOnly && !isNonCompliant) return false

      const matchesMineDraft = !mineOnlyDrafts
        ? true
        : s === 'draft' && createdByEmail === myEmail

      const matchesStatus = status === 'All' || s === status
      const matchesStage = stage === 'All' || stg === stage
      const matchesComm = communityCode === 'All' || cc === communityCode
      const matchesQ =
        !qLower ||
        (x.number || '').toLowerCase().includes(qLower) ||
        (x.title || '').toLowerCase().includes(qLower)

      return matchesMineDraft && matchesStatus && matchesStage && matchesComm && matchesQ
    })
  }, [
    cafItems,
    status,
    stage,
    communityCode,
    q,
    mineOnlyDrafts,
    myEmail,
    pendingOnly,
    nonCompliantOnly,
  ])

  // ---------- PM filtering (NEW) ----------
  const filteredPmTemplates = useMemo(() => {
    const sel = communityCode
    if (!sel || sel === 'All') return pmTemplates || []

    // If user picked a "code", resolve its id; if user picked an "id", resolve its code.
    const selAsId = communityCodeToId.get(sel) || sel
    const selAsCode = communityIdToCode.get(sel) || sel

    return (pmTemplates || []).filter(pm => {
      const pmCode = pm.communityCode || ''
      const pmId = pm.communityId || (Array.isArray(pm.communityIds) ? pm.communityIds[0] : '') || ''
      return pmCode === selAsCode || pmId === selAsId || pmId === sel
    })
  }, [pmTemplates, communityCode, communityCodeToId, communityIdToCode])

  // ---------- Metrics from filtered set ----------
  const totals = items.length
  const drafts = items.filter(x => (x.status || '').toLowerCase() === 'draft')
  const approvedOrComplete = items.filter(x => {
    const s = (x.status || '').toLowerCase()
    return s === 'approved' || s === 'complete'
  })
  const pending = items.filter(x => {
    const s = (x.status || '').toLowerCase()
    return !['approved', 'complete', 'draft'].includes(s)
  })
  const nonCompliant = items.filter(x => !hasMin3WithPdf(x))

  const avgProposals = items.length
    ? (
        items.reduce((sum, x) => sum + (x.proposals?.length || 0), 0) / items.length
      ).toFixed(2)
    : '0.00'

  const byStage = items.reduce((acc, x) => {
    const k = x.stage || '—'
    acc[k] = (acc[k] || 0) + 1
    return acc
  }, {})

  function plannedFor(caf) {
    const v = caf.contractPrice ?? caf.projectBudget ?? caf.projectBudgetAmount ?? 0
    return Number(v || 0)
  }
  function actualFor(caf) {
    const v = caf.projectActual ?? caf.actualProjectAmount ?? caf.projectActualAmount ?? 0
    return Number(v || 0)
  }

  const totalPlannedSpend = items.reduce((sum, x) => sum + plannedFor(x), 0)
  const totalActualSpend = items.reduce((sum, x) => sum + actualFor(x), 0)

  const varianceSamples = items
    .map(c => {
      const p = plannedFor(c)
      const a = actualFor(c)
      if (!p && !a) return null
      return a - p
    })
    .filter(v => v !== null)

  const avgVariance =
    varianceSamples.length > 0
      ? varianceSamples.reduce((s, v) => s + v, 0) / varianceSamples.length
      : null

  const closedCafs = items.filter(x => {
    const s = (x.status || '').toLowerCase()
    return s === 'approved' || s === 'complete'
  })

  const closedWithBudget = closedCafs.filter(c => {
    const p = plannedFor(c)
    const a = actualFor(c)
    return Number.isFinite(p) && Number.isFinite(a) && (p || a)
  })
  const closedOverBudget = closedWithBudget.filter(c => actualFor(c) > plannedFor(c))

  const approvalDurations = items
    .filter(x => x.status === 'approved' && x.approvals?.history?.length)
    .map(x => {
      const submitted = x.approvals.history.find(h => h.action === 'submitted')
      const approved = x.approvals.history.find(h => h.action === 'approved')
      if (!submitted || !approved) return null

      const submittedAt = new Date(submitted.ts)
      const approvedAt = new Date(approved.ts)
      if (Number.isNaN(submittedAt.getTime()) || Number.isNaN(approvedAt.getTime())) {
        return null
      }

      const days = (approvedAt.getTime() - submittedAt.getTime()) / (1000 * 60 * 60 * 24)
      return { days, approvedAt }
    })
    .filter(Boolean)

  const approvalTimeAvg = approvalDurations.length
    ? (
        approvalDurations.reduce((sum, d) => sum + d.days, 0) / approvalDurations.length
      ).toFixed(1)
    : '—'

  const NOW_MS = Date.now()
  const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000

  const recentDurations = approvalDurations.filter(
    d => NOW_MS - d.approvedAt.getTime() <= THIRTY_DAYS_MS,
  )

  const approvalTimeAvg30d = recentDurations.length
    ? (
        recentDurations.reduce((sum, d) => sum + d.days, 0) / recentDurations.length
      ).toFixed(1)
    : '—'

  const sortByCreated = (a, b) => {
    const aD = new Date(normalizeCreatedAt(a.createdAt) || 0).getTime()
    const bD = new Date(normalizeCreatedAt(b.createdAt) || 0).getTime()
    return bD - aD
  }

  // ------- SLA flags -------
  const SLA_DAYS = 7
  const SLA_MS = SLA_DAYS * 24 * 60 * 60 * 1000

  const pendingForSla = items.filter(x => {
    const s = (x.status || '').toLowerCase()
    return s !== 'draft' && s !== 'approved' && s !== 'complete'
  })

  const slaBreaches = pendingForSla.filter(x => {
    const submitted = x.approvals?.history?.find(h => h.action === 'submitted')
    if (!submitted) return false
    const submittedAt = new Date(submitted.ts)
    if (Number.isNaN(submittedAt.getTime())) return false
    return NOW_MS - submittedAt.getTime() > SLA_MS
  })

  const slaBreachesLabel = pendingForSla.length
    ? `${slaBreaches.length}/${pendingForSla.length}`
    : '0/0'

  // ---------- PM KPIs (NOW community-filtered) ----------
  const thisYear = new Date().getFullYear()
  const startOfYear = new Date(thisYear, 0, 1)
  const startOfNextYear = new Date(thisYear + 1, 0, 1)

  const pmsCreatedYtd = (filteredPmTemplates || []).filter(t => {
    const d = toDateOrNull(t.createdAt)
    return d && d >= startOfYear && d < startOfNextYear
  }).length

  const pmSentToEntrataYtd = (filteredPmTemplates || []).reduce((acc, pm) => {
    return acc + (pmHasEntrataSendInRange(pm, startOfYear, startOfNextYear) ? 1 : 0)
  }, 0)

  // ---------- Presets ----------
  function clearAll() {
    setStatus('All')
    setStage('All')
    setCommunityCode('All')
    setQ('')
    setMineOnlyDrafts(false)
    setPendingOnly(false)
    setNonCompliantOnly(false)
  }

  if (loading) {
    return (
      <div className="dashboard-loading" role="status">Loading capital projects…</div>
    )
  }

  return (
    <div>
      {/* Preset filters */}
      <div
        className="card"
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 8,
          alignItems: 'center',
        }}
      >
        <strong>Presets:</strong>

        <button className="preset-btn" onClick={clearAll}>
          All
        </button>

        <button
          className="preset-btn"
          onClick={() => {
            setStatus('All')
            setStage('All')
            setCommunityCode('All')
            setQ('')
            setMineOnlyDrafts(false)
            setPendingOnly(false)
            setNonCompliantOnly(true)
          }}
        >
          Non-Compliant
        </button>

        <button
          className="preset-btn"
          onClick={() => {
            setStatus('draft')
            setStage('All')
            setCommunityCode('All')
            setQ('')
            setMineOnlyDrafts(true)
            setPendingOnly(false)
            setNonCompliantOnly(false)
          }}
        >
          My Drafts
        </button>

        <button
          className="preset-btn"
          onClick={() => {
            setStatus('All')
            setStage('All')
            setCommunityCode('All')
            setQ('')
            setMineOnlyDrafts(false)
            setNonCompliantOnly(false)
            setPendingOnly(true)
          }}
        >
          Pending Approvals
        </button>

        <button
          className="preset-btn"
          onClick={() => {
            setStatus('complete')
            setStage('All')
            setCommunityCode('All')
            setQ('')
            setMineOnlyDrafts(false)
            setPendingOnly(false)
            setNonCompliantOnly(false)
          }}
        >
          Completed
        </button>
        <button style={{marginLeft:'auto'}} onClick={() => exportCsv(items, getCommunityName)}>Export CSV</button>
      </div>

      {/* Filters */}
      <div
        className="mobile-stack-grid card"
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 2fr 2fr auto',
          gap: 8,
          alignItems: 'end',
        }}
      >
        <div>
          <label>Status</label>
          <select
            value={status}
            onChange={e => {
              setStatus(e.target.value)
              if (e.target.value !== 'draft') setMineOnlyDrafts(false)
            }}
          >
            {STATUS_OPTIONS.map(s => (
              <option key={s} value={s}>
                {s === 'All' ? 'All Statuses' : s}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label>Stage</label>
          <select value={stage} onChange={e => setStage(e.target.value)}>
            {stageOptions.map(s => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label>Community</label>
          <select value={communityCode} onChange={e => setCommunityCode(e.target.value)}>
            {commOptions.map(c => (
              <option key={c.code} value={c.code}>
                {c.communityName}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label>Search</label>
          <input
            placeholder="CAF # or Title"
            value={q}
            onChange={e => setQ(e.target.value)}
          />
        </div>

        <div>
          <button onClick={clearAll}>Clear</button>
        </div>

        {status === 'draft' && (
          <div
            style={{
              gridColumn: '1 / -1',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <label className="muted">
              <input
                type="checkbox"
                checked={mineOnlyDrafts}
                onChange={e => setMineOnlyDrafts(e.target.checked)}
                style={{ marginRight: 6 }}
              />
              Show only drafts I created
            </label>
          </div>
        )}

        {status === 'All' && (
          <div
            style={{
              gridColumn: '1 / -1',
              display: 'flex',
              gap: 16,
              alignItems: 'center',
            }}
          >
            <label className="muted">
              <input
                type="checkbox"
                checked={pendingOnly}
                onChange={e => setPendingOnly(e.target.checked)}
                style={{ marginRight: 6 }}
              />
              Show only pending approvals (not Approved, Complete, or Draft)
            </label>

            <label className="muted">
              <input
                type="checkbox"
                checked={nonCompliantOnly}
                onChange={e => setNonCompliantOnly(e.target.checked)}
                style={{ marginRight: 6 }}
              />
              Show only non-compliant (fewer than 3 PDFs)
            </label>
          </div>
        )}
      </div>

      {/* KPI tiles */}
      <div
        className="kpi-grid capital-dashboard-metrics"
        style={{
          display: 'grid',
          gap: 12,
          marginTop: 12,
        }}
      >
        <MetricCard
          label="Total CAFs"
          value={items.length}
          tooltip="Count of CAFs after applying filters. Formula: total = number of filtered CAF records."
        />
        <MetricCard
          label="Non-Compliant CAFs"
          value={nonCompliant.length}
          tooltip="Count of CAFs with < 3 proposals. Formula: nonCompliant = count(caf.proposals.length < 3)."
        />
        <MetricCard
          label="Pending Approvals"
          value={pending.length}
          tooltip="Count of CAFs in approval flow. Formula: status not in {approved, complete, draft}."
        />
        <MetricCard
          label="Approved / Complete"
          value={approvedOrComplete.length}
          tooltip="Count of CAFs with status in {approved, complete}."
        />
        <MetricCard
          label="Total Planned Spend"
          value={formatCurrency(totalPlannedSpend)}
          tooltip="Sum of planned spend across filtered CAFs. Formula: sum(planned), where planned = contractPrice or projectBudget."
        />
        <MetricCard
          label="Total Actual Spend"
          value={formatCurrency(totalActualSpend)}
          tooltip="Sum of actual spend across filtered CAFs. Formula: sum(actual), using projectActual fields."
        />
        <MetricCard
          label="Average Variance"
          value={avgVariance == null ? '—' : formatCurrency(avgVariance)}
          tooltip="Average variance where both planned and actual exist. Formula: avg(actual - planned). Positive = over budget."
        />
        <MetricCard
          label="Closed Over Budget"
          value={`${closedOverBudget.length}/${closedWithBudget.length}`}
          tooltip="Numerator: closed CAFs with actual > planned. Denominator: closed CAFs with both values. Formula: count(actual > planned) / count(has actual & planned)."
        />
        <MetricCard
          label="Avg Approval"
          value={approvalTimeAvg === '—' ? '—' : `${approvalTimeAvg} days`}
          tooltip="Average approval duration in days. Formula: avg(approvedAt - submittedAt) for approved CAFs."
        />
        <MetricCard
          label="Avg Approval · 30d"
          value={approvalTimeAvg30d === '—' ? '—' : `${approvalTimeAvg30d} days`}
          tooltip="Average approval duration for CAFs approved in last 30 days. Formula: avg(approvedAt - submittedAt)."
        />
        <MetricCard
          label={`SLA Breaches (>${SLA_DAYS}d)`}
          value={slaBreachesLabel}
          tooltip={`Breaches/Pending. Breach = pending CAF with (today - submittedAt) > ${SLA_DAYS} days. Pending = CAFs still in approval flow.`}
        />
        <MetricCard
          label="Avg Proposals / CAF"
          value={avgProposals}
          tooltip="Average proposals per CAF. Formula: sum(proposalsCount) / count(CAFs)."
        />
        <MetricCard
          label="PMs Created YTD"
          value={pmsCreatedYtd}
          tooltip="Count of PM templates created this calendar year, filtered by community."
        />
        <MetricCard
          label="PMs to Entrata YTD"
          value={pmSentToEntrataYtd}
          tooltip="Count of PM templates with at least one successful Entrata sync this calendar year, filtered by community."
        />
      </div>

      {/* Stage breakdown */}
      <div className="card" style={{ marginTop: 12 }}>
        <h3>By Stage</h3>
        <div className="pillWrap">
          {Object.entries(byStage).map(([s, count]) => (
            <span className="pill" key={s}>
              {s}: {count}
            </span>
          ))}
        </div>
      </div>

      {/* Pending Approvals */}
      <div className="card" style={{ marginTop: 12 }}>
        <h3>Pending Approvals (Top 10)</h3>
        <div className="responsive-table-scroll" tabIndex={0} role="region" aria-label="Scrollable table"><table className="table">
          <thead>
            <tr>
              <th>CAF #</th>
              <th>Community</th>
              <th>Title</th>
              <th>Stage</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {pending.sort(sortByCreated).slice(0, 10).map(x => (
              <tr key={x.id}>
                <td>
                  <Link to={`/dev/caf/${encodeURIComponent(x.id)}`}>
                    {x.number}
                  </Link>
                </td>
                <td>{getCommunityName(x.communityCode)}</td>
                <td>{x.title}</td>
                <td>{x.stage || '—'}</td>
                <td>
                  <span className="badge">{x.status}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>
      </div>

      {/* Non-compliant list */}
      <div className="card" style={{ marginTop: 12 }}>
        <h3>Non-Compliant CAFs (Top 10)</h3>
        <div className="responsive-table-scroll" tabIndex={0} role="region" aria-label="Scrollable table"><table className="table">
          <thead>
            <tr>
              <th>CAF #</th>
              <th>Community</th>
              <th>Title</th>
              <th>Proposals</th>
              <th>Reason</th>
            </tr>
          </thead>
          <tbody>
            {nonCompliant
              .sort(sortByCreated)
              .slice(0, 10)
              .map(x => {
                const proposals = x.proposals || []
                const missing = proposals.filter(
                  p => !(p.file && (p.file.dataUrl || p.file.downloadURL)),
                ).length
                const reason =
                  proposals.length < 3
                    ? `${proposals.length} proposals`
                    : `Missing PDFs on ${missing} proposal(s)`
                return (
                  <tr key={x.id}>
                    <td>
                      <Link to={`/dev/caf/${encodeURIComponent(x.id)}`}>
                        {x.number}
                      </Link>
                    </td>
                    <td>{getCommunityName(x.communityCode)}</td>
                    <td>{x.title}</td>
                    <td>{proposals.length}</td>
                    <td>{reason}</td>
                  </tr>
                )
              })}
          </tbody>
        </table></div>
      </div>

      {/* Recent Drafts */}
      <div className="card" style={{ marginTop: 12 }}>
        <h3>Recent Drafts (Top 10)</h3>
        <div className="responsive-table-scroll" tabIndex={0} role="region" aria-label="Scrollable table"><table className="table">
          <thead>
            <tr>
              <th>CAF #</th>
              <th>Community</th>
              <th>Title</th>
              <th>Created</th>
            </tr>
          </thead>
          <tbody>
            {drafts.sort(sortByCreated).slice(0, 10).map(x => (
              <tr key={x.id}>
                <td>
                  <Link to={`/dev/caf/${encodeURIComponent(x.id)}`}>
                    {x.number}
                  </Link>
                </td>
                <td>{getCommunityName(x.communityCode)}</td>
                <td>{x.title}</td>
                <td>{normalizeCreatedAt(x.createdAt) || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table></div>
      </div>
    </div>
  )
}

/* --- KPI card component --- */
function MetricCard({ label, value, tooltip }) {
  return (
    <div
      className="kpi-card"
      data-tooltip={tooltip} tabIndex={0}
      style={{
        minHeight: 120,
        padding: '16px 12px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
      }}
    >
      <strong>{value}</strong>
      <span>{label}</span>
    </div>
  )
}
